"use client";

import { useTranslations } from "next-intl";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import Modal from "@components/core/Modal";
import Button from "@components/core/Button";
import Image from "@components/core/Image";
import { useCurrentUser } from "@hooks/use-current-user";
import { useTranslationsForLocale } from "@hooks/use-translations-for-locale";
import { putLanguage } from "@utils/call-api/users-call";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { locales, defaultLocale } from "@lib/i18n/config";
import { useLocale } from "next-intl";

function UserLangModal() {
  const t = useTranslations("common");

  const user = useCurrentUser();
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams().toString();

  const userLang = user?.user_tags?.find((tag) => tag.key == "lang")?.value || "fr";
  const tUserLang = useTranslationsForLocale(userLang, "common");
  const [showModal, setShowModal] = useState(false);

  const flagSrc = (flagLocale) => {
    switch (flagLocale) {
      case "fr":
        return "franceFlag";
      case "de":
        return "germanyFlag";
      case "nl":
        return "netherlandsFlag";
      case "en":
        return "unitedKingdomFlag";
      case "it":
        return "italyFlag";
      case "es":
        return "spainFlag";
      case "pt":
        return "portugalFlag";
      default:
        return "franceFlag";
    }
  };

  const flagImage = (flagLocale) => {
    const src = flagSrc(flagLocale);
    return (
      <Image
        src={src}
        alt={src}
      />
    );
  };

  function sendEvent(button) {
    const eventData = { button, page: pathname, user_lang: userLang, locale };

    analytics.track("userlangmodal", eventData);
    ahoy("userlangmodal", eventData);
  }

  function changeLocaleToUserLang() {
    sendEvent("UserLangModal.redirect_btn");

    Cookies.set("NEXT_LOCALE", userLang, { expires: 3650 });

    let pathWithoutLocale = pathname;
    if (pathname.startsWith(`/${locale}`)) {
      pathWithoutLocale = pathname.slice(`/${locale}`.length) || "/";
    }

    let newUrl = userLang === defaultLocale ? "" : `/${userLang}`;
    newUrl += pathWithoutLocale;
    if (searchParams) newUrl += `?${searchParams}`;

    router.push(newUrl);
  }

  async function changeUserLang(newLang) {
    setShowModal(false);
    await putLanguage(newLang);

    Cookies.set("NEXT_LOCALE", newLang, { expires: 3650 });
    sendEvent("UserLangModal.change_lang_btn");
    router.refresh();
  }

  useEffect(() => {
    setShowModal(user && userLang !== locale);
  }, [user, userLang, locale]);

  if (locales.length < 2) return null;

  return (
    <Modal
      show={showModal}
      closeButton={false}
      style={{ zIndex: 3000 }}
    >
      <div className="p-3">
        <div className="container pt-4 text-center">
          <h5>{tUserLang("user_lang_modal.title")}</h5>

          <p>{tUserLang("user_lang_modal.ask_for_redirection")}</p>

          <Button
            variant="link-primary"
            onClick={changeLocaleToUserLang}
            title={tUserLang("user_lang_modal.redirect_lang_btn")}
          >
            {flagImage(userLang)}
            <span className="px-2">{tUserLang("user_lang_modal.redirect_lang_btn")}</span>
          </Button>
        </div>

        <hr className="mx-4 mb-4 border border-middle-gray" />

        <div className="container pb-3 text-center">
          <h5>{t("user_lang_modal.title")}</h5>

          <p>{t(`user_lang_modal.ask_change_lang.${userLang}`)}</p>

          <Button
            variant="link-primary"
            onClick={() => changeUserLang(locale)}
            title={t("user_lang_modal.change_lang_btn")}
          >
            {flagImage(locale)}
            <span className="px-2">{t("user_lang_modal.change_lang_btn")}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default UserLangModal;
